import React from 'react';

import { GridApiPro } from '@mui/x-data-grid-pro';

import { UserRole } from 'core/constants';

import API from 'utils/api';
import { RiskActionStatus } from 'utils/api/modules/risks';
import { readerDayKey, utcMidnight } from 'utils/formatters/timezone';
import { useLocalStorage } from 'utils/hooks/localStorage';

import { FilterType } from 'components/Grid/types';

import type { AssignedToFilterValue } from 'containers/Tasks/components/AssignedToPicker';
import type { DueDateRange } from 'containers/Tasks/components/DueDatePicker';
import {
  arraysEqual,
  FilterItem,
  FILTER_FIELDS,
  FILTER_OPERATORS,
  getDefaultAssignedTo,
} from 'containers/Tasks/hooks/types';

import type { RiskActionTypeFilterValue } from './RiskActionTypePicker';
import { DEFAULT_STATUS_FILTER } from './statuses';

export const STORAGE_KEY = 'actions_risks_filters';

export interface SavedRiskActionFilters {
  type: RiskActionTypeFilterValue;
  assignedTo: AssignedToFilterValue[];
  dueDate: DueDateRange;
  status: RiskActionStatus[];
}

type User = { uuid: string; name: string; role: UserRole };

const dayBound = (value: number | null): number | null => (value === null ? null : utcMidnight(readerDayKey(value)));

const items = {
  type: (value: RiskActionTypeFilterValue): FilterItem[] =>
    value === 'all'
      ? []
      : [{ id: FILTER_FIELDS.TYPE, field: FILTER_FIELDS.TYPE, operator: FILTER_OPERATORS.EQUALS, value }],
  status: (values: RiskActionStatus[]): FilterItem[] =>
    values.map((value) => ({
      id: `${FILTER_FIELDS.STATUS}_${value}`,
      field: FILTER_FIELDS.STATUS,
      operator: FILTER_OPERATORS.IN,
      value,
    })),
  assignedTo: (values: AssignedToFilterValue[]): FilterItem[] =>
    values.map(({ uuid }) => ({
      id: `${FILTER_FIELDS.ASSIGNED_TO}_${uuid}`,
      field: FILTER_FIELDS.ASSIGNED_TO,
      operator: FILTER_OPERATORS.IN,
      value: uuid,
    })),
  dueDate: ({ from, to }: DueDateRange): FilterItem[] =>
    from === null && to === null
      ? []
      : [
          {
            id: FILTER_FIELDS.DUE_DATE,
            field: FILTER_FIELDS.DUE_DATE,
            operator: FILTER_OPERATORS.RANGE,
            value: [dayBound(from) ?? 0, dayBound(to)],
          },
        ],
};

const allItems = (filters: SavedRiskActionFilters): FilterItem[] => [
  ...items.type(filters.type),
  ...items.assignedTo(filters.assignedTo),
  ...items.dueDate(filters.dueDate),
  ...items.status(filters.status),
];

const withAssignee = (filters: SavedRiskActionFilters, user: User): SavedRiskActionFilters =>
  user.role === UserRole.User || filters.assignedTo.length === 0
    ? { ...filters, assignedTo: getDefaultAssignedTo(user) }
    : filters;

export const useRiskActionsFilters = ({
  gridApiRef,
  refreshRef,
  user,
  defaultDueDate,
}: {
  gridApiRef: React.RefObject<GridApiPro>;
  refreshRef: React.MutableRefObject<(() => void) | null>;
  user: User;
  defaultDueDate: DueDateRange;
}) => {
  const defaults: SavedRiskActionFilters = {
    type: 'all',
    assignedTo: getDefaultAssignedTo(user),
    dueDate: defaultDueDate,
    status: DEFAULT_STATUS_FILTER,
  };

  const [stored, setStored] = useLocalStorage<SavedRiskActionFilters>(STORAGE_KEY, defaults);
  // As Audits: an empty Assigned to ("All") falls back to the reader when the page loads, not while it is open.
  const [initial] = React.useState(() => withAssignee(stored, user));
  const [synced, setSynced] = React.useState(false);
  const filters = synced ? stored : initial;

  const applyToGrid = (next: SavedRiskActionFilters): void => {
    if (!gridApiRef.current) return;
    const kept = gridApiRef.current.state.filter.filterModel.items.filter(
      (item) => !Object.values(FILTER_FIELDS).some((field) => field === item.field)
    );
    gridApiRef.current.setFilterModel({
      ...gridApiRef.current.state.filter.filterModel,
      items: [...kept, ...allItems(next)],
    });
    refreshRef.current?.();
  };

  React.useEffect(() => {
    setStored(initial);
    setSynced(true);
    API.user
      .loadGridPreferences(STORAGE_KEY)()
      .then((response) => {
        const saved = response.data.state as Partial<SavedRiskActionFilters>;
        const merged = withAssignee(
          {
            type: saved.type ?? defaults.type,
            assignedTo: saved.assignedTo ?? [],
            dueDate: saved.dueDate ?? defaults.dueDate,
            status: saved.status ?? defaults.status,
          },
          user
        );
        setStored(merged);
        applyToGrid(merged);
      })
      .catch(() => {});
  }, []);

  const [initialFilters] = React.useState(() => allItems(initial) as unknown as FilterType[]);

  const update = (next: SavedRiskActionFilters): void => {
    setStored(next);
    applyToGrid(next);
    API.user
      .saveGridPreferences(STORAGE_KEY, next)()
      .catch(() => {});
  };

  const change =
    <K extends keyof SavedRiskActionFilters>(key: K) =>
    (value: SavedRiskActionFilters[K]): void =>
      update({ ...filters, [key]: value });

  const hasActiveFilters =
    filters.type !== defaults.type ||
    filters.dueDate.from !== defaults.dueDate.from ||
    filters.dueDate.to !== defaults.dueDate.to ||
    !arraysEqual(filters.status, defaults.status) ||
    !(filters.assignedTo.length === 1 && filters.assignedTo[0].uuid === user.uuid);

  return {
    filters,
    initialFilters,
    hasActiveFilters,
    handleTypeChange: change('type'),
    handleAssignedToChange: change('assignedTo'),
    handleDueDateChange: change('dueDate'),
    handleStatusChange: change('status'),
    handleResetFilters: () => update(defaults),
  };
};
